import { get, post } from './API';
import {
  AWAITING_STATE, UNKNOWN_REASONS, reasonLabel, repliedDescriptor,
} from './EmailInboxService';
import { filingReason } from '../Utils/inboundTriage';
// The field-message reader, imported from the one place that owns it rather
// than re-spelled: `apiFieldMessages` knows where a ValidationError from this
// API actually puts its reason, and a second copy would be a second thing to
// fix when the server's refusal shape changes. See its own header.
import { apiFieldMessages } from './AiInstructionsService';

/**
 * THE DESK'S OWN STATE ABOUT A CONVERSATION — who holds it, how it was
 * closed, the draft an agent has waiting on it, and what triage decided.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ONE RESOURCE, `/ai/drafts`, AND IT SERVES TWO SURFACES.
 * ══════════════════════════════════════════════════════════════════════════
 *
 *   GET  /ai/drafts?from&to&limit         the window's entries
 *   GET  /ai/drafts?conversation_id=<id>  one, with its draft and its run
 *   POST /ai/drafts/claim    { conversation_id }
 *   POST /ai/drafts/release  { conversation_id }
 *   POST /ai/drafts/resolve  { conversation_id, resolution, sent_text? }
 *
 * AN EARLIER VERSION OF THIS FILE ASKED FOR `/customer-service/desk/*` AND
 * THAT WAS THE WRONG HALF OF A RECONCILIATION. The claim, release and resolve
 * writes were already built and deployed on `/ai/drafts` — the same three
 * decisions, over the same two collections, addressed by the same id — so a
 * second resource would have been a second set of routes to keep in step, a
 * second Lambda to deploy and a second claim of what a resolution is called.
 * The API grew the one thing it was missing (the window read) and this file
 * now speaks to it. Nothing is served twice.
 *
 * AND A RESOURCE IS NOT FREE HERE: every API Gateway resource adds its rights
 * to the CI execution user's managed policy, which sits within ~70 characters
 * of IAM's 6144-byte limit. The four writes are sub-paths of a `{proxy+}`
 * child, which is free once the child exists.
 *
 * ── NOTHING HERE SENDS AN EMAIL, AND A RESOLUTION IS NOT A SEND ──
 * There is no outbound transport in this phase on either side: the API's
 * `DraftCheckNoSend` test walks the real import graph from `/ai/drafts` and
 * `DraftReviewService` to hold that, and there is no send call in this file to
 * hold on ours. `resolve` RECORDS what a person did — including that they sent
 * the agent's draft — and makes nothing go out. What happens to an accepted
 * text is a later phase's question and deliberately not answered here, which
 * is why there is no send method to disable.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE RESOLUTIONS ARE THE SERVER'S FOUR, NOT A PAIR OF OUR OWN.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * This file used to offer `replied | no_reply_needed` and a separate
 * `approve | discard` decision on the draft. Both were spellings of the one
 * closed set the API records — `DRAFT_RESOLUTIONS` — and the collapse was the
 * expensive half: the comparison the owner asked for ("logging how well the ai
 * transformed vs human") IS a count of the four apart from each other, and
 * `replied` cannot tell "they sent the agent's draft" from "they wrote their
 * own". Reporting both as one word would have made the number he wants stop
 * existing. `booking.category` is what that costs in this codebase: 170
 * spellings of a free-text field and ~36% of the volume lost to any
 * per-category analysis.
 *
 * So the five things a reviewer can do map onto the four words one-to-one:
 *
 *   Approve this wording  → `used_draft`
 *   Accept my edit        → `used_draft` + `sent_text`
 *   Close — replied       → `wrote_own_reply`
 *   Close — no reply      → `no_reply_needed`
 *   Reject the draft      → `rejected`
 *
 * ── A RESOLUTION IS FINAL FOR THE DRAFT IT IS ABOUT, INCLUDING `rejected` ──
 * The server refuses a second resolution of a conversation already resolved
 * FOR THE CURRENT DRAFT, and refuses a claim on one, because reopening it
 * would discard the resolution. A NEW customer email reopens the thread by
 * itself: it produces a new drafting run, and the claim filter admits a
 * resolved row whose run differs. So there is no reopen control here — a
 * button that could only ever raise a 409 is worse than no button — and
 * `rejected` is offered as what it is, a closing decision that records the
 * draft was unusable and that nothing was sent.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * `entry.triage` IS THE FUNNEL'S VERDICT, SURFACED PER CONVERSATION — AND IT
 * IS THE PIECE THE QUEUE WAS MISSING ENTIRELY.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Every inbound email writes `ai_decisions` rows under one `run_id`, and a
 * refusal carries a `drop_reason` from a closed set. The archive endpoint
 * reads none of that — `GET /email-inbox` filters by mailbox and nothing else
 * — so every automated email that ever arrived is in the list whatever triage
 * concluded. This field is the join, and it is asked for HERE rather than done
 * in the browser, for two separate reasons:
 *
 *   `GET /ai/decisions` IS ADMINISTRATOR-ONLY. It is gated on `ManageAi`,
 *   which `UserAccess.helper` grants to Administrator and to no other role —
 *   pinned by `UserAccess.manageAi.helper.test.ts` — so the desk, which is
 *   `Agent` / `AgentHead`, cannot call it at all. `/ai/drafts` deliberately is
 *   NOT gated on it, which is what makes this whole file possible.
 *
 *   AND NO FILTER REACHES A CONVERSATION ANYWAY, which is the reason that
 *   survives somebody being given the permission:
 *   `AiDecisionListFilters.schema.ts` offers `subject_reference` (a BOOKING
 *   reference) and `run_id`, and a conversation has neither in hand.
 *
 *   `outcome`     `eligible` | `refused`. There is no third value, and the
 *                 FIELD'S ABSENCE is the third answer — see below.
 *   `drop_reason` one of the nine `INBOUND_DROP_REASONS`, on a refusal.
 *   `kinds`       the model's own categories, only when `drop_reason` is
 *                 `not_eligible`. They live on the eligibility step's
 *                 `output.kinds` and are NOT drop reasons — there are nine
 *                 drop reasons and four kinds, not thirteen of either.
 *
 * `triage: null` MEANS NOBODY LOOKED, and it is the majority case by a very
 * long way: triage is new, is never run backwards (the age guard is what stops
 * a replay drafting against ten years of mail), and ~44,766 archived emails
 * therefore have no verdict. It must not read as `eligible` — the queue would
 * be the noisy one again — and must not read as `refused`, which would claim a
 * decision nothing made.
 *
 * `triage_epoch` on the list read is the instant before which nothing was ever
 * triaged, so the console can say why the archive looks unfiltered. It is
 * SERVED rather than mirrored here on purpose: it is a code-owned constant
 * whose entire job is to be hard to change, and a second copy in a bundle that
 * deploys separately would be the one that silently disagrees.
 *
 * THE LIST READ IS NOT KEYED BY THE IDS ON SCREEN, deliberately. A
 * conversation id is a Message-ID or an S3 key, 50–80 characters of it, so
 * fifty of them in a query string is several kilobytes against API Gateway's
 * 10KB request-line budget — and a POST for a read is a worse thing to ask
 * somebody to build. The desk's rows are FEW instead: one exists only where a
 * person claimed, a person closed, or an agent ran. So the window's worth
 * comes back in one call and the console joins by id, and the response says
 * when the cap cut it (`truncated`) rather than letting a row whose entry was
 * left behind quietly read as unclaimed.
 *
 * ── THE VOCABULARY IS BORROWED, NOT INVENTED ──
 * A draft's `state` is `AiDecision.state` (`AiActivityService.DECISION_STATES`)
 * and a run's `trace` steps are dry-run trace steps
 * (`AiDryRunService.TRACE_OUTCOMES`). The drafting pipeline already records
 * every one of those, so a third spelling of "what the model did" would be a
 * third thing to keep in step.
 *
 * ── THE ONE TRANSLATION THIS FILE MAKES, AND WHERE IT IS ──
 * The server answers with its own `review` view — `state`, `claimed_by`,
 * `claimed_at`, `claim_expires_at`, `resolution`, `resolved_by`,
 * `resolved_at`, in one shape shared by the window read, the single read and
 * the body of a 409. `entryOf` below turns that into the two questions a
 * screen asks — `holder` and `resolution` — and it is the ONLY place that
 * does, so a queue row and the pane it opens cannot disagree about who is on a
 * conversation.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHAT `draft.text` ACTUALLY CONTAINS — READ THIS BEFORE ADDING ANY PARSING
 * TO IT. THE ANSWER IS NOW ON THE WIRE, SO THERE IS NOTHING LEFT TO GUESS.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `GET /ai/drafts` SERVES THE CONTRACT. `LatestDraft` carries
 * `draft_contract` (one of `DRAFT_CONTRACT` below) and `narration` beside
 * `draft`, so the console is TOLD what the text is instead of inferring it.
 * Until 2026-10-27 it was not, and the card warned "NOT A CLEAN REPLY" over
 * every draft alike — including the ones that came through the reply tool,
 * which production says is now nearly all of them: 10 of the 10 runs that
 * produced a draft in the first 18 hours after `submit_draft` deployed
 * recorded `submitted` (`Pickup-Hungary-prod-Ai-AutonomousDraft`, 2026-10-27).
 *
 * THE CONSOLE STILL PARSES NOTHING, and that rule did not need the field —
 * it needed it to STOP being a guess. Splitting the text on a `---` or on a
 * phrase is the one forbidden move: a wrong split either hides the answer or
 * hands a reviewer the agent's deliberation as sendable text. The server
 * already did the split, on its own row, with the model's own markers.
 *
 * `narration` IS NOT PART OF THE REPLY AND NEVER BECOMES PART OF IT. The API
 * keeps it in its own key for that reason (`LatestDraft.narration`, and
 * `DraftAnswer.narration` for both invariants); this file carries it across in
 * its own key for the same one, and `Components/DraftSuggestion` labels it
 * rather than blending it in. The owner asked for exactly that: "I am seeing
 * reasoning that i like, but make it clear what's part of the draft reply, vs
 * what's not."
 *
 * AND A RUN WITH NO DRAFT IS STILL THE COMMON CASE. From the 40-email
 * evaluation of 2026-10-26: 29 of 40 runs failed outright on Bedrock
 * `ServiceUnavailableException`, and a failed run records no draft at all. So
 * `draft: null` WITH a run present is ordinary, and it means "the agent could
 * not produce one" — which the console must say in those words. Rendered as a
 * gap it would read as "no draft was needed", which is a different and much
 * better piece of news than the truth.
 *
 * A TRUNCATED RUN IS NO LONGER ONE OF THOSE, which is the other half of what
 * changed. `latestDraftFor` matches `output.draft_contract` now, so a run cut
 * off mid-answer arrives here as a draft row with `draft: null` and
 * `draft_contract: 'truncated'` — and the fragment, `output.partial_draft`, is
 * deliberately NOT served, so there is nothing on this shape that could be
 * mistaken for a whole reply. Before that, such a row was invisible to the
 * single read and the pane showed the PREVIOUS run's draft: a reply to the
 * message before the one on screen.
 *
 * ── AND "NO REPLY NEEDED" IS A FLAG AFTER ALL ──
 * An earlier version of this file said there was no such field and that the
 * only reading of it was a draft whose whole text was `**No reply needed.**`.
 * There is one: the server parses its own sentinel and answers
 * `draft.no_reply_needed` — the model's kind, or `null` for "it said so and
 * named no reason this codebase recognises" — with `state: 'no_action'`
 * beside it. So a run that concluded nothing needed answering is rendered as
 * that and not as a failure, and closing the conversation stays a RESOLUTION a
 * person records.
 */

/** The paths the contract above names. Here so a test can assert them once. */
export const DRAFTS_PATH = '/ai/drafts';
export const CLAIM_PATH = `${DRAFTS_PATH}/claim`;
export const RELEASE_PATH = `${DRAFTS_PATH}/release`;
export const RESOLVE_PATH = `${DRAFTS_PATH}/resolve`;

/**
 * HOW A CONVERSATION CAN BE CLOSED — the server's `DRAFT_RESOLUTIONS`, in its
 * order, with this screen's words for them.
 *
 * `no_reply_needed` IS A SUCCESS AND MUST NEVER RENDER AS A FAILURE. A
 * newsletter, a supplier's receipt, a thank-you with nothing in it: the right
 * handling is to close it and send nothing, and a desk screen that can only
 * express "replied" leaves every one of those sitting in the Awaiting count
 * for ever — which is how the one number this screen exists to make
 * trustworthy stops being trusted. It is its own outcome, its own word and its
 * own view in the queue (see `DESK_VIEWS`), with the same `good` tone as a
 * reply.
 *
 * `rejected` IS THE ONE THAT IS NOT GOOD NEWS, and it carries the amber this
 * screen reserves for "a human is needed": nothing went out and the customer
 * may still be waiting. It is still a closing decision — see the header — and
 * the detail says so rather than leaving a reader to infer it from the tone.
 */
export const RESOLUTIONS = [
  { value: 'used_draft', label: 'Sent the agent’s draft', tone: 'good' },
  { value: 'wrote_own_reply', label: 'Replied in our own words', tone: 'good' },
  { value: 'no_reply_needed', label: 'No reply needed', tone: 'good' },
  { value: 'rejected', label: 'Draft rejected', tone: 'warn' },
];

/**
 * The four by name, for the call sites that mean one in particular. One object
 * rather than four exports, and the strings are the server's enum.
 */
export const RESOLUTION = {
  USED_DRAFT: 'used_draft',
  WROTE_OWN_REPLY: 'wrote_own_reply',
  NO_REPLY_NEEDED: 'no_reply_needed',
  REJECTED: 'rejected',
};

/**
 * WHICH CHANNEL THE AGENT'S REPLY ARRIVED BY — the server's `DRAFT_CONTRACTS`,
 * by name, for the call sites that mean one in particular.
 *
 * Borrowed, not invented, exactly like `RESOLUTION` above: the words are the
 * API's closed set and a second spelling of them would be a second thing to
 * keep in step. What each one means is argued in `Constants/DraftReview.ts`
 * (`DRAFT_CONTRACTS`) and must not be re-argued here; what this screen DOES
 * with each is in `Components/DraftSuggestion`.
 *
 * AN ABSENT CONTRACT IS A FIFTH CASE AND IT IS NOT IN THIS OBJECT, because it
 * is the absence of a value rather than a value: a row written before the
 * field existed, or one whose word the API did not recognise and dropped. It
 * must read as "we were not told" and keep the conservative rendering.
 */
export const DRAFT_CONTRACT = {
  SUBMITTED: 'submitted',
  DELIMITED: 'delimited',
  UNDELIMITED: 'undelimited',
  TRUNCATED: 'truncated',
};

/** The two the server accepts a `sent_text` with: the ones that sent something. */
const CARRIES_SENT_TEXT = [RESOLUTION.USED_DRAFT, RESOLUTION.WROTE_OWN_REPLY];

/** A descriptor by value, so an outcome the server adds later still renders. */
export const resolutionDescriptor = (value) => (
  RESOLUTIONS.find((entry) => entry.value === value)
  || (value ? { value, label: String(value), tone: 'muted' } : null)
);

/**
 * WHAT ONE CONVERSATION IS TO THE DESK, RIGHT NOW — the single decision the
 * queue, the thread header and the filters all read.
 *
 * It lives here and not in a component because three places render it and a
 * second copy would let the row and the pane it opens disagree about who is
 * on it, which is the exact failure the holder field exists to prevent.
 *
 * ── THE ORDER OF PRECEDENCE IS THE DESIGN ──
 *
 *   1. A PERSON'S RESOLUTION WINS, unless a NEWER RUN has superseded it —
 *      see below. It is a decision a person made, and it outranks anything the
 *      archive infers about whether a reply went out: a conversation closed as
 *      `no_reply_needed` has nothing outbound in it by definition, and must not
 *      read as "waiting" because of that.
 *   2. A HOLDER IS NEXT, because stopping a second person acting is what the
 *      field is for. It outranks a pending draft: "Anna is on it" is the
 *      sentence that prevents the double send, and it must not be hidden
 *      behind "a draft is ready". It also outranks the agent's own conclusion,
 *      for the reason `DESK_VIEWS` gives about `triage: null`: once a person
 *      has taken a conversation, the machine's opinion of it stops being the
 *      interesting fact.
 *   3. A PENDING DRAFT, because the work is already half done and that is
 *      worth seeing before an agent picks up something from scratch.
 *   4. THE AGENT'S OWN "NOTHING TO ANSWER", which is a real outcome and not a
 *      gap — "no action is okay action". It is NOT a person's decision and
 *      does not claim to be: it carries no author, and the detail says who
 *      reached it, so a reviewer and whoever reads the ratio later can both
 *      tell.
 *   5. THE ARCHIVE'S OWN READING, last. `awaiting`, `replied`, `unknown` —
 *      the three states `EmailInboxService` resolves, which are all the desk
 *      has to go on where nobody has touched a conversation.
 *
 * ── WHY A DRAFT OUTRANKS THE AGENT'S OWN CONCLUSION ──
 * From the server the two cannot both be set: one run's outcome is EITHER a
 * draft or "nothing to answer", and the read derives both from that one row.
 * So this order only ever decides a case the server does not produce — and it
 * is ordered on the cost of being wrong, which is asymmetric. "No reply
 * needed" drawn over a draft somebody should read hides work and files a live
 * enquiry away; "Draft ready" drawn over a settled thread costs one click. If
 * a later shape ever does carry both, the thing that needs a person to look at
 * it wins.
 *
 * ── A NEWER RUN SUPERSEDES AN OUTCOME ABOUT AN EARLIER MESSAGE ──
 * A conversation accumulates a drafting run per inbound email, so a customer
 * who writes again after their thread was closed has a question nobody has
 * answered — and the server already agrees: `claim` reopens a resolved row
 * whose `run_id` differs from the newest draft's. A screen that kept drawing
 * "Closed" over that would be the one place disagreeing with what taking the
 * conversation would actually do, and the cost of the error is a live enquiry
 * nobody sees. So a resolution whose run is not the newest run falls through
 * to whatever is true now.
 *
 * IT NEEDS BOTH RUNS TO FIRE. A window read whose cap cut the drafting rows
 * leaves the newest run unknown, and "we cannot say" must not read as
 * "superseded" — that would reopen every closed conversation on screen.
 *
 * THE STATE OF A CLOSED CONVERSATION IS HOW IT WAS CLOSED, which is why the
 * value is the resolution itself rather than a flat `resolved`: the queue's
 * views filter on these values, and "closed with nothing to answer" and
 * "closed with the draft rejected" are the two a supervisor looks for.
 *
 * `mine` is decided by comparing the holder with the session's email, which
 * is a RENDERING decision and not the enforcement: the server owns that, and
 * says so by refusing a claim on a held conversation with a 409 (see
 * `isHeldByAnother`). A browser that got the comparison wrong would draw the
 * wrong button and still be unable to take the conversation.
 *
 * `detail` is the half of the state that is a fact rather than a word — who
 * holds it, who closed it, why it could not be judged — and it is never
 * dropped: an `unknown` with no cause attached is indistinguishable from this
 * screen being broken, which is why that reason is folded in HERE rather than
 * special-cased in the badge. One function answers the whole question and
 * `StateLabel` only draws what it returns.
 */
export const deskState = (conversation, entry, me) => {
  const byAgent = entry?.agent_resolution;
  // The run the newest outcome belongs to, whichever kind of outcome it was.
  // `null` means the rows were not read, never "there is no newer run".
  const newestRun = entry?.draft?.run_id || byAgent?.run_id || null;

  const resolution = entry?.resolution;
  const superseded = !!resolution?.run_id && !!newestRun && resolution.run_id !== newestRun;

  if (resolution && !superseded) {
    const outcome = resolutionDescriptor(resolution.outcome);
    return {
      value: resolution.outcome || 'resolved',
      label: outcome?.label || 'Resolved',
      tone: outcome?.tone || 'good',
      detail: resolution.by ? `by ${resolution.by}` : '',
    };
  }

  const holder = entry?.holder;
  if (holder?.email) {
    const isMine = !!me && holder.email.toLowerCase() === String(me).toLowerCase();
    if (isMine) {
      return {
        value: 'mine', label: 'With me', tone: 'changed', detail: '',
      };
    }
    return {
      value: 'theirs',
      label: 'Taken',
      tone: 'muted',
      detail: holder.email,
    };
  }

  if (entry?.draft) {
    return {
      value: 'draft_ready', label: 'Draft ready', tone: 'warn', detail: '',
    };
  }

  if (byAgent) {
    const outcome = resolutionDescriptor(byAgent.outcome);
    return {
      value: byAgent.outcome,
      label: outcome?.label || 'No reply needed',
      tone: outcome?.tone || 'good',
      // WHO REACHED IT, always, because this is the half that distinguishes it
      // from a person's identical outcome. A blank here would make the two
      // render the same, which is the collapse the record exists to avoid.
      detail: 'the agent judged so',
    };
  }

  // FILED, NOT WAITING. Triage refused this one, so nothing is pending on a
  // person and it must not draw amber: a newsletter rendered as "Needs a
  // reply" is the noise the owner is complaining about, and it is also what
  // keeps the one number this console exists for permanently wrong. It sits
  // BELOW a resolution, a holder and a draft because each of those is a later
  // and more specific fact about the same conversation.
  if (entry?.triage?.outcome === 'refused') {
    return {
      value: 'filed',
      label: 'Filed',
      tone: 'muted',
      detail: filingReason(entry.triage),
    };
  }

  if (conversation?.replied_state === AWAITING_STATE) {
    return {
      value: 'needs_reply', label: 'Needs a reply', tone: 'warn', detail: '',
    };
  }

  if (conversation?.replied_state === 'replied') {
    return {
      value: 'answered', label: 'Answered', tone: 'good', detail: '',
    };
  }

  // THE SERVER'S OWN VALUE IS KEPT, not collapsed onto `unknown`. A replied
  // state this build does not recognise would then be claimed by the Unjudged
  // view and read as "no threading headers", which is a different and
  // flattering answer; unplaced, it appears under Everything and nowhere else.
  const replied = repliedDescriptor(conversation?.replied_state);
  return {
    value: replied?.value || 'unknown',
    label: replied?.label || 'Unknown',
    tone: replied?.tone || 'muted',
    detail: reasonLabel(UNKNOWN_REASONS, conversation?.unknown_reason),
  };
};

/**
 * THE QUEUE'S VIEWS — the questions a desk actually asks, not the states the
 * archive happens to store.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * NEEDS US IS THE DEFAULT, AND IT IS WHAT TRIAGE PASSED. That is the whole
 * answer to "shouldn't no-reply and automated emails bypass through the
 * analyzer ai? i am seeing a lot showing up in CS".
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Every inbound email now produces a verdict and, when refused, a reason from
 * a closed set — and until this view existed the queue simply did not read
 * it, so every newsletter the gates had already caught was still sitting in
 * the list looking like unanswered customer mail.
 *
 * A REFUSED EMAIL IS FILED, NOT HIDDEN. `Filed` is a real view with the
 * reason on every row and a reason filter inside it, because the point of
 * recording why an email was refused is that "why did nobody answer this"
 * must be answerable in one click. A gate whose output cannot be inspected is
 * a gate quietly eating enquiries, and it looks exactly like a gate that is
 * working.
 *
 * ── AND MOST OF THE ARCHIVE HAS NO VERDICT AT ALL ──
 * Triage started at its epoch and is deliberately never run backwards, so of
 * ~44,766 archived emails almost none carry one. That is why `Not triaged`
 * exists as its own view rather than those conversations being folded into
 * either side: filtering them into `Needs us` is the noisy screen this change
 * is undoing, and filing them under `Filed` would be claiming a refusal
 * nothing ever decided. They are a third thing — "nobody looked" — and the
 * console says so and gives the epoch it reads from the server.
 *
 * A FAILED RUN IS NOT A REFUSAL. A Bedrock throttle (29 of 40 runs in the
 * production evaluation) leaves a conversation eligible with no draft, which
 * means "we do not know yet" and stays in `Needs us` where a person will see
 * it. Nothing about a failure may file an email away.
 *
 * ── THE THREE DIMENSIONS, AND WHICH LAYER ANSWERS EACH ──
 *
 *   `server`  what the view pushes to `GET /email-inbox`, which can filter on
 *             `replied` and nothing else.
 *   `states`  which derived desk states it keeps of the page that comes back
 *             (`null` keeps everything). Narrowed in the browser, because the
 *             archive knows nothing about holders, resolutions or drafts.
 *   `triage`  which verdict it keeps: `eligible`, `refused`, `none` (no
 *             verdict at all) or `null` for any. Also narrowed in the browser,
 *             off the entry the console already holds.
 *
 * Because two of the three only narrow what is LOADED, the count beside a
 * view carries the same `+` the awaiting count always has: it counts what is
 * loaded, and a number read as "in the archive" is a number nobody should
 * trust.
 *
 * `Mine`, `Draft ready` and `No reply needed` take `triage: null` on purpose. Once a
 * person has taken a conversation or closed it, the machine's opinion about
 * whether it deserved attention is no longer the interesting fact — and a view
 * that hid somebody's own claimed conversation because a gate disliked it
 * would be the filter fighting the desk.
 */
export const DESK_VIEWS = [
  {
    value: 'needs-us',
    label: 'Needs us',
    server: { replied: AWAITING_STATE },
    states: ['needs_reply', 'draft_ready'],
    triage: 'eligible',
  },
  {
    value: 'mine', label: 'Mine', server: {}, states: ['mine'], triage: null,
  },
  {
    value: 'draft-ready',
    label: 'Draft ready',
    server: {},
    states: ['draft_ready'],
    triage: null,
  },
  {
    /* ITS OWN VIEW, kept: closing a conversation with nothing sent is the
       right handling of a newsletter or a thank-you, and a desk that cannot
       see what it correctly ignored stops trusting the Awaiting count. The
       other three resolutions appear under Everything, with the badge saying
       which — there is deliberately no view per word.

       IT HOLDS BOTH THE PERSON'S AND THE AGENT'S, because they are the same
       outcome and "no action is okay action" was asked for as a visible,
       filterable one. The badge's detail says which reached it; the view does
       not need to split them, and splitting it would hide half of the answer
       to "what did we correctly ignore". */
    value: 'no-reply-needed',
    label: 'No reply needed',
    server: {},
    states: [RESOLUTION.NO_REPLY_NEEDED],
    triage: null,
  },
  {
    /* The only view with a second control inside it: the reason filter, over
       the closed set the funnel records. See Utils/inboundTriage. */
    value: 'filed', label: 'Filed', server: {}, states: null, triage: 'refused',
  },
  {
    value: 'not-triaged', label: 'Not triaged', server: {}, states: null, triage: 'none',
  },
  {
    value: 'everything', label: 'Everything', server: {}, states: null, triage: null,
  },
];

/** The view the console opens on: eligible work that nobody has taken. */
export const DEFAULT_VIEW = DESK_VIEWS[0].value;

/** The one view that carries its own reason filter. */
export const FILED_VIEW = 'filed';

/** A view by value, falling back to the default so a stale value still lands. */
export const resolveView = (value) => (
  DESK_VIEWS.find((view) => view.value === value) || DESK_VIEWS[0]
);

/**
 * Does this conversation belong in this view?
 *
 * Both dimensions, and the triage one treats a MISSING verdict as its own
 * answer rather than as a failing `eligible` test — which is the whole reason
 * `none` is a value. Without it every untriaged conversation would quietly
 * match nothing and the default view would be empty for a reason no reader
 * could see.
 */
export const viewKeeps = (view, state, entry) => {
  if (view?.states && !view.states.includes(state?.value)) return false;
  if (!view?.triage) return true;

  const outcome = entry?.triage?.outcome;
  return view.triage === 'none' ? !outcome : outcome === view.triage;
};

/** `response.data.data`, with the one legacy nesting tolerated. */
const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

/** Empty values dropped, so a cleared filter is absent rather than `?from=`. */
const compact = (params) => Object.fromEntries(
  Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
);

/**
 * ONE ENTRY, with every field defaulted and the server's `review` read as the
 * two questions a screen asks.
 *
 * `holder`, `resolution`, `draft` and `triage` are NULL rather than absent
 * when there is none, because each of them is a question `deskState` asks
 * directly and `undefined` there would make a missing key and an empty answer
 * the same thing. A reset jest mock answering `undefined` therefore renders an
 * unclaimed conversation rather than throwing.
 *
 * `review.state` IS NOT CONSULTED, and that is deliberate: `claimed_by` and
 * `resolution` are the facts, and `open` is the state of a row that has
 * neither. Reading the word as well would be two readings of one thing, and
 * the two would disagree the first time a release raced a resolution.
 */
const entryOf = (raw) => {
  const review = raw?.review || null;
  const byAgent = raw?.agent_resolution || null;

  return {
    conversation_id: raw?.conversation_id || '',
    holder: review?.claimed_by
      ? { email: review.claimed_by, since: review.claimed_at || null }
      : null,
    resolution: review?.resolution
      ? {
        outcome: review.resolution,
        by: review.resolved_by || '',
        at: review.resolved_at || null,
        // THE RUN IT WAS ABOUT, which is what makes a stale resolution
        // visible: the server reopens a resolved conversation whose newest
        // run differs, so a screen that drew it as closed would disagree with
        // what claiming it would actually do. See `deskState`.
        run_id: review.run_id || null,
      }
      : null,
    // THE AGENT'S OWN, in its own field and never folded into `resolution`.
    // No `by` and no `at` of a person's, because nobody decided it — and an
    // absent author as the only signal would make "the agent decided" look
    // like a human resolution whose author was lost.
    agent_resolution: byAgent
      ? {
        outcome: byAgent.resolution,
        run_id: byAgent.run_id || null,
        at: byAgent.at || null,
        kind: byAgent.kind ?? null,
      }
      : null,
    draft: raw?.draft || null,
    triage: raw?.triage || null,
  };
};

/**
 * The badge's worth of a draft, off the single-conversation read.
 *
 * PRESENT ONLY WHERE THERE IS TEXT. A run that failed, or that concluded no
 * reply was needed, has none — and `deskState` reads this field's presence as
 * "Draft ready", which over either of those would send a reviewer looking for
 * something nobody wrote. It is the same test the server applies when it
 * builds the queue's badge (`output.draft`), so the row and the pane it opens
 * agree.
 */
const draftBadgeOf = (draft) => (
  typeof draft?.draft === 'string' && draft.run_id
    ? { run_id: draft.run_id, state: draft.state }
    : null
);

/**
 * Is this refusal "somebody else got there first"?
 *
 * 409 and nothing else. The server owns the holder and is the only thing that
 * can know, so the screen asks and then repaints from what the refusal carries
 * rather than guessing — two agents pressing Take within a second of each
 * other is the whole case this is for, and the loser has to end up looking at
 * the truth.
 */
export const isHeldByAnother = (err) => err?.response?.status === 409;

/**
 * THE REVIEW A REFUSAL CARRIES, so the loser of a race repaints from the truth
 * rather than from a guess.
 *
 * A 409 from this API is `{ ...details, message }` — `ConflictError` spreads
 * its details into the body — and the details are the same facts the review
 * view holds: who claimed it and when, or how it was resolved and by whom.
 * `status` is the server's own discriminator between the two, so the state is
 * READ rather than inferred from which fields happen to be present.
 *
 * IT ANSWERS A REVIEW AND NOT AN ENTRY, which is the half it actually knows. A
 * refusal says nothing about the draft or the triage verdict, and an entry
 * built here would blank both — turning "somebody else has this" into "and the
 * draft is gone". The caller merges this into the entry it already holds.
 *
 * `null` when there is nothing on it, which a screen must treat as "ask again"
 * rather than as "nobody holds it".
 */
export const reviewFromError = (err) => {
  const body = err?.response?.data?.data ?? err?.response?.data;

  if (!body?.status) return null;

  const resolved = body.status === 'already_resolved';

  return {
    state: resolved ? 'resolved' : 'claimed',
    ...(body.run_id ? { run_id: body.run_id } : {}),
    ...(body.claimed_by ? { claimed_by: body.claimed_by } : {}),
    ...(body.claimed_at ? { claimed_at: body.claimed_at } : {}),
    ...(body.claim_expires_at ? { claim_expires_at: body.claim_expires_at } : {}),
    ...(body.resolution ? { resolution: body.resolution } : {}),
    ...(body.resolved_by ? { resolved_by: body.resolved_by } : {}),
  };
};

/**
 * THE SAME ENTRY WITH A NEWER REVIEW ON IT — what the loser of a claim race
 * repaints from.
 *
 * MERGED AND NOT REPLACED. A refusal carries the review and says nothing about
 * the draft, the agent's own conclusion or the triage verdict, so an entry
 * rebuilt from it alone would turn "somebody else has this" into "and the
 * draft is gone". Those three are carried over untouched; nothing in a 409
 * contradicts any of them.
 *
 * It is here rather than in the page because `entryOf` is the one reading of a
 * review, and a second one at a call site would be the copy that drifts.
 */
export const withReview = (entry, review) => ({
  ...entryOf({ conversation_id: entry?.conversation_id, review }),
  draft: entry?.draft || null,
  agent_resolution: entry?.agent_resolution || null,
  triage: entry?.triage || null,
});

/**
 * Why the desk refused, as an agent should read it.
 *
 * Field messages first, for the reason the instruction builder's save reads
 * them the same way: a ValidationError from this API puts a constant string in
 * `message` and the actual reason in `errors[]`. A 409's own `message` names
 * the colleague who has it, which is the sentence the second person needs.
 */
export const deskErrorText = (err, fallback = 'The desk could not be updated.') => (
  apiFieldMessages(err).join('; ')
  || err?.response?.data?.message
  || err?.message
  || fallback
);

/**
 * THE AGENT'S OWN "NOTHING TO ANSWER", off a single read's draft.
 *
 * The window read is served this field; the single read is served the draft it
 * is derived from, and deriving it here rather than asking the server for it
 * again is what keeps the queue's badge and the thread header's badge the same
 * sentence. The alternative was a fourth query on the path a Gmail add-on
 * polls, for a field that surface does not draw.
 *
 * THE KEY AND NOT THE VALUE, because present-but-`null` is a real answer: the
 * agent said no reply was needed and named no reason this codebase recognises.
 * `latestDraftFor` reads it the same way, which is why this can.
 */
const agentResolutionOf = (draft) => (
  draft?.run_id && draft.no_reply_needed !== undefined
    ? {
      resolution: RESOLUTION.NO_REPLY_NEEDED,
      run_id: draft.run_id,
      at: draft.occurred_at || null,
      kind: draft.no_reply_needed,
    }
    : null
);

/** The entry a read or a write answers with, off `{ conversation_id, review, draft }`. */
const entryFromWrite = (body) => entryOf({
  conversation_id: body?.conversation_id,
  review: body?.review,
  draft: draftBadgeOf(body?.draft),
  agent_resolution: agentResolutionOf(body?.draft),
});

const CustomerServiceDeskService = {
  /**
   * The desk's entries for one window, newest activity first.
   *
   * Keyed to the SAME date range the conversation list is showing, so the
   * join covers what is on screen. `truncated` is the server saying the cap
   * cut the set — the console must then say that desk state is missing from
   * the older rows rather than draw them as unclaimed.
   */
  listEntries: async (filters = {}) => {
    const body = unwrap(await get(DRAFTS_PATH, compact(filters)));
    return {
      entries: (body.entries || []).map(entryOf),
      truncated: !!body.truncated,
      // Null rather than a date of our own when the server did not say. The
      // console then omits the sentence instead of printing an epoch it
      // guessed, which on this particular fact would be worse than silence.
      triageEpoch: body.triage_epoch || null,
    };
  },

  /**
   * One conversation's desk state IN FULL — the entry, the draft and the run
   * that produced it.
   *
   * One call rather than three because they are one story and the right-hand
   * pane shows all of it at once: a reviewer decides whether to trust a draft
   * from what the agent looked up, so splitting the trace off would mean the
   * draft could paint before the evidence for it.
   *
   * `draft` AND `run` ARE INDEPENDENT, and all four combinations are real:
   *
   *   run + draft   the ordinary case. The suggestion and its evidence.
   *   run, no draft THE COMMON CASE TODAY — 29 of 40 runs failed on a Bedrock
   *                 throttle. `run.state` and `run.error` are what the console
   *                 shows instead, in words, because a blank space here reads
   *                 as "nothing needed saying".
   *   draft, no run a draft recorded before run tracing, or by a path that
   *                 does not trace. The suggestion stands; the trust panel is
   *                 absent rather than empty.
   *   neither       no agent has looked at this conversation. Nothing to say,
   *                 and the console says nothing — which is the one case where
   *                 silence is the honest rendering.
   *
   * THE ENTRY CARRIES NO TRIAGE VERDICT. This read is addressed by one
   * conversation and answers about the review, the draft and the run; the
   * funnel's verdict comes off the window read, which the queue already holds,
   * and the caller composes the two. Asking for it again here would be a
   * fourth query on the path a Gmail add-on polls, for a field that screen
   * does not draw.
   */
  getConversation: async (conversationId) => {
    const body = unwrap(await get(DRAFTS_PATH, { conversation_id: conversationId }));

    return {
      entry: entryFromWrite(body),
      // THE THREE ROWS THAT ARE A CARD, and a truncated run is the third of
      // them: it carries no `draft` and no `no_reply_needed` by design — the
      // fragment must not be servable as a reply — so it is recognised by its
      // contract word instead. Without this clause the new field would be
      // dead on arrival and the pane would draw a cut-off run as a run that
      // produced nothing.
      draft: typeof body.draft?.draft === 'string'
        || body.draft?.no_reply_needed !== undefined
        || body.draft?.draft_contract === DRAFT_CONTRACT.TRUNCATED
        ? {
          run_id: body.draft.run_id,
          state: body.draft.state,
          text: body.draft.draft,
          created_at: body.draft.occurred_at,
          // WHAT THE TEXT ACTUALLY IS, carried rather than inferred. Absent
          // when the server sent none, which the card reads as "we were not
          // told" — never as a clean reply. See the header.
          ...(body.draft.draft_contract === undefined
            ? {}
            : { draft_contract: body.draft.draft_contract }),
          // ITS OWN KEY, ALL THE WAY THROUGH. It is the agent's reasoning and
          // it is never part of what could be sent; joining it onto `text`
          // for a screen's convenience reintroduces the bug the whole
          // contract exists to fix.
          ...(body.draft.narration === undefined
            ? {}
            : { narration: body.draft.narration }),
          // PRESENT-BUT-NULL IS A REAL VALUE: "it said no reply was needed and
          // named no reason this codebase recognises". `?? null` here would
          // erase nothing, but reading the KEY is what the server does and the
          // two must agree.
          ...(body.draft.no_reply_needed === undefined
            ? {}
            : { no_reply_needed: body.draft.no_reply_needed }),
        }
        : null,
      run: body.run || null,
    };
  },

  /**
   * Take it. Refused with a 409, and the review it carries, when somebody
   * else holds it.
   *
   * IT IS NOT THE REOPEN. The server refuses a claim on a conversation already
   * resolved for the current draft, because reopening it would discard the
   * resolution — a new customer email reopens the thread by producing a new
   * drafting run. See the header.
   */
  claim: async (conversationId) => entryFromWrite(
    unwrap(await post(CLAIM_PATH, { conversation_id: conversationId })),
  ),

  /** Put it back in the queue. Only your own claim; a stale one expires. */
  release: async (conversationId) => entryFromWrite(
    unwrap(await post(RELEASE_PATH, { conversation_id: conversationId })),
  ),

  /**
   * Close it, and say which of the four it was.
   *
   * `sentText` IS SENT ONLY WITH THE TWO RESOLUTIONS THAT SENT SOMETHING, and
   * the server refuses it beside the other two rather than ignoring it: a
   * "what the human sent" recorded against a thread where nothing was sent
   * would make every rate computed off it wrong in the direction that looks
   * fine. The filter is here as well as there so a mis-click cannot turn into
   * a 400 the person has to read.
   */
  resolve: async (conversationId, resolution, sentText) => entryFromWrite(
    unwrap(await post(RESOLVE_PATH, compact({
      conversation_id: conversationId,
      resolution,
      sent_text: CARRIES_SENT_TEXT.includes(resolution) ? sentText : undefined,
    }))),
  ),
};

export default CustomerServiceDeskService;
